import { FC, useContext, useMemo, useState } from "react";
import { Navigate, Outlet, Route, RouteObject, RouterProvider, Routes, createBrowserRouter, useParams, useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/react";

import TrialsProvider from '@/components/providers/data/TrialsProvider';
import TrialProvider from '@/components/providers/data/TrialProvider';
import InstitutionsProvider from '@/components/providers/data/InstitutionsProvider';
import InstitutionProvider from '@/components/providers/data/InstitutionProvider';
import MasterGroupsProvider from '@/components/providers/data/MasterGroupsProvider';
import CoordinatingGroupsProvider from '@/components/providers/data/CoordinatingGroupsProvider';
import MasterGroupProvider from '@/components/providers/data/MasterGroupProvider';
import CoordinatingGroupProvider from '@/components/providers/data/CoordinatingGroupProvider';
import ParticipantsProvider from "@/components/providers/data/ParticipantsProvider";
import ParticipantProvider from "@/components/providers/data/ParticipantProvider";
import MetadataProvider from "@/components/providers/data/MetadataProvider";
import TrialConfigurationSourceDataProvider from "@/components/providers/data/TrialConfigurationSourceDataProvider";

import DefaultLayout from '@/components/layouts/DefaultLayout';

import Trials from '@/pages/Trials';
import Participants from '@/pages/Participants';
import Institutions from '@/pages/Institutions';
import TrialNotesAndQueries from '@/pages/TrialNotesAndQueries';
import InstitutionNotesAndQueries from "@/pages/InstitutionNotesAndQueries";
import Configuration from "@/pages/configuration/Configuration";
import ConfigurationTrial from "@/pages/configuration/ConfigurationTrial";
import ConfigurationInstitutions from "@/pages/configuration/ConfigurationInstitutions";
import ConfigurationMasterGroups from "@/pages/configuration/ConfigurationMasterGroups";
import ConfigurationCoordinatingGroups from "@/pages/configuration/ConfigurationCoordinatingGroups";
import ConfigurationParticipants from "@/pages/configuration/ConfigurationParticipants";
import ConfigurationMasterGroup from "@/pages/configuration/ConfigurationMasterGroup";
import ConfigurationCoordinatingGroup from "@/pages/configuration/ConfigurationCoordinatingGroup";
import ConfigurationInstitution from "@/pages/configuration/ConfigurationInstitution";
import ConfigurationParticipant from "@/pages/configuration/ConfigurationParticipant";
import ConfigurationSourceDatas from "@/pages/configuration/ConfigurationSourceDatas";
import ConfigurationSourceData from "@/pages/configuration/ConfigurationSourceData";
import ConfigurationSourceDataDetails from "@/pages/configuration/ConfigurationSourceDataDetails";
import ConfigurationSourceDataEvents from "@/pages/configuration/ConfigurationSourceDataEvents";
import ConfigurationSourceDataForms from "@/pages/configuration/ConfigurationSourceDataForms";
import ConfigurationSourceDataEvent from "@/pages/configuration/ConfigurationSourceDataEvent";
import ConfigurationSourceDataFormVersions from "@/pages/configuration/ConfigurationSourceDataFormVersions";
import ConfigurationSourceDataItems from "@/pages/configuration/ConfigurationSourceDataItems";
import ConfigurationSourceDataItemValues from "@/pages/configuration/ConfigurationSourceDataItemValues";
import ConfigurationSourceDataForm from "./pages/configuration/ConfigurationSourceDataForm";
import ConfigurationSourceDataFormVersion from "./pages/configuration/ConfigurationSourceDataFormVersion";
import ConfigurationSourceDataItemGroup from "./pages/configuration/ConfigurationSourceDataItemGroup";
import ConfigurationSourceDataItem from "./pages/configuration/ConfigurationSourceDataItem";
import ConfigurationSourceDataItemValue from "./pages/configuration/ConfigurationSourceDataItemValue";
import ParticipantSourceData from "./pages/ParticipantSourceData";
import ParticipantSourceDataEvents from "./pages/ParticipantSourceDataEvents";
import ParticipantSourceDataForms from "./pages/ParticipantSourceDataForms";
import ParticipantSourceDataForm from "./pages/ParticipantSourceDataForm";
import ParticipantNotesAndQueries from "./pages/ParticipantNotesAndQueries";
import ParticipantSourceDataEventNotesAndQueries from "./pages/ParticipantSourceDataEventNotesAndQueries";
import ParticipantSourceDataFormNotesAndQueries from "./pages/ParticipantSourceDataFormNotesAndQueries";
import ParticipantSourceDataItemValueNotesAndQueries from "./pages/ParticipantSourceDataItemValueNotesAndQueries";
import ParticipantSourceDataItemValueNotesAndQueriesAdd from "./pages/ParticipantSourceDataItemValueNotesAndQueriesAdd";
import ParticipantSourceDataEventSourceDataFindings from "./pages/ParticipantSourceDataEventSourceDataFindings";
import ParticipantSourceDataFormSourceDataFindings from "./pages/ParticipantSourceDataFormSourceDataFindings";
import ParticipantSourceDataItemValueSourceDataFindings from "./pages/ParticipantSourceDataItemValueSourceDataFindings";
import ParticipantSourceDataFindings from "./pages/ParticipantSourceDataFindings";
import InstitutionSourceDataFindings from "./pages/InstitutionSourceDataFindings";
import TrialSourceDataFindings from "./pages/TrialSourceDataFindings";
import ParticipantSourceDataItemValueSourceDataFindingsAdd from "./pages/ParticipantSourceDataItemValueSourceDataFindingsAdd";
import ParticipantSourceDataItemValueSourceDataFindingsEdit from "./pages/ParticipantSourceDataItemValueSourceDataFindingsEdit";
import ParticipantSourceDataFindingsAdd from "./pages/ParticipantSourceDataFindingsAdd";
import ParticipantSourceDataFindingsEdit from "./pages/ParticipantSourceDataFindingsEdit";
import InstitutionSourceDataFindingsAdd from "./pages/InstitutionSourceDataFindingsAdd";
import InstitutionSourceDataFindingsEdit from "./pages/InstitutionSourceDataFindingsEdit";
import TrialSourceDataFindingsAdd from "./pages/TrialSourceDataFindingsAdd";
import TrialSourceDataFindingsEdit from "./pages/TrialSourceDataFindingsEdit";
import ParticipantSourceDataFormTable from "./pages/ParticipantSourceDataFormTable";
import ParticipantSourceDataFormTableV2 from "./pages/ParticipantSourceDataFormTableV2";
import ParticipantSourceDataItemValueAudit from "./pages/ParticipantSourceDataItemValueAudit";
import ParticipantSourceDataItemValueVerificationAudit from "./pages/ParticipantSourceDataItemValueVerificationAudit";
import ParticipantSourceDataItemValueReviewAudit from "./pages/ParticipantSourceDataItemValueReviewAudit";
import ParticipantSourceDataItemValueAuditAndNotes from "./pages/ParticipantSourceDataItemValueAuditAndNotes";
import ShowLayoutProvider from "./components/providers/layouts/ShowLayoutProvider";
import { ShowLayoutContext } from "./contexts/layouts/ShowLayoutContext";
import { Box } from "@mui/material";
import SourceDataReport from "./pages/SourceDataReport";
import ConfigurationUtilities from "./pages/configuration/ConfigurationUtilities";
import TrialNotesAndQueriesBulkAdd from "./pages/TrialNotesAndQueriesBulkAdd";
import RouteErrorBoundary from "./components/errors/RouteErrorBoundary";
import ParticipantSourceDataItemValueNotApplicableAudit from "./pages/ParticipantSourceDataItemValueNotApplicableAudit";
import AuthenticatedUserContext from "./contexts/authentication/AuthenticatedUserContext";

const RedirectTrial: FC = () => {
    const {trialOid} = useParams()

    return (
        <Navigate to={`/${trialOid}/institutions`} replace />
    )
}

const RedirectInstitution: FC = () => {
    const {trialOid, institutionCode} = useParams()

    return (
        <Navigate to={`/${trialOid}/institutions/${institutionCode}/participants`} replace />
    )
}

const RedirectParticipantSourceData: FC = () => {
    const {trialOid, institutionCode, studyNumber} = useParams()

    return (
        <Navigate to={`/${trialOid}/institutions/${institutionCode}/participants/${studyNumber}/events`} replace />
    )
}

const RedirectParticipantSourceDataForms: FC = () => {
    const {trialOid, institutionCode, studyNumber, studyEventOid, studyEventRepeat} = useParams()

    return (
        <Navigate to={`/${trialOid}/institutions/${institutionCode}/participants/${studyNumber}/events/${studyEventOid}/${studyEventRepeat}/forms`} replace />
    )
}

const RedirectConfigurationSourceData: FC = () => {
    const {trialOid, configurationSourceDataId} = useParams()

    return (
        <Navigate to={`/${trialOid}/configuration/source-data/${configurationSourceDataId}/select`} replace />
    )
}

const RedirectConfigurationSourceDataEvent: FC = () => {
    const {trialOid, configurationSourceDataId, studyEventOid} = useParams()

    return (
        <Navigate to={`/${trialOid}/configuration/source-data/${configurationSourceDataId}/events/${studyEventOid}/forms`} replace />
    )
}

const RedirectConfigurationSourceDataItemGroup: FC = () => {
    const {trialOid, configurationSourceDataId, studyEventOid, caseReportFormOid, caseReportFormVersionOid} = useParams()

    return (
        <Navigate to={`/${trialOid}/configuration/source-data/${configurationSourceDataId}${studyEventOid ? `/events/${studyEventOid}` : ''}/forms/${caseReportFormOid}/versions/${caseReportFormVersionOid}/items`} replace />
    )
}

const LayoutSwitcher = () => {
    const { showLayout } = useContext(ShowLayoutContext);

    if (showLayout)
    {
        return <DefaultLayout />;
    }

    return (
        <Box
            sx={{
                padding: 2
            }}
        >
            <Outlet />
        </Box>
    );
}

const AppLayout = () => {


    return (
        <TrialsProvider>
            <TrialProvider>
                <MetadataProvider>
                    <TrialConfigurationSourceDataProvider>
                        <InstitutionsProvider>
                            <InstitutionProvider>
                                <MasterGroupsProvider>
                                    <CoordinatingGroupsProvider>
                                        <CoordinatingGroupProvider>
                                            <MasterGroupProvider>
                                                <ParticipantsProvider>
                                                    <ParticipantProvider>
                                                        <ShowLayoutProvider>
                                                            <LayoutSwitcher />
                                                        </ShowLayoutProvider>
                                                    </ParticipantProvider>
                                                </ParticipantsProvider>
                                            </MasterGroupProvider>
                                        </CoordinatingGroupProvider>
                                    </CoordinatingGroupsProvider>
                                </MasterGroupsProvider>
                            </InstitutionProvider>
                        </InstitutionsProvider>
                    </TrialConfigurationSourceDataProvider>
                </MetadataProvider>
            </TrialProvider>
        </TrialsProvider>
    )
}

const participantSourceDataRoutes: RouteObject = {
    path: 'events',
    children: [
        {
            index: true,
            element: <ParticipantSourceDataEvents />,
        },
        {
            path: ':studyEventOid',
            children: [
                {
                    path: ':studyEventRepeat',
                    children: [
                        {
                            index: true,
                            element: <RedirectParticipantSourceDataForms />,
                        },
                        {
                            path: 'notes-and-queries',
                            element: <ParticipantSourceDataEventNotesAndQueries />
                        },
                        {
                            path: 'findings',
                            element: <ParticipantSourceDataEventSourceDataFindings />
                        },
                        {
                            path: 'forms',
                            children: [
                                {
                                    index: true,
                                    element: <ParticipantSourceDataForms />,
                                },
                                {
                                    path: ':caseReportFormOid',
                                    children: [
                                        {
                                            index: true,
                                            element: <ParticipantSourceDataForm />
                                        },
                                        {
                                            path: 'notes-and-queries',
                                            element: <ParticipantSourceDataFormNotesAndQueries />
                                        },
                                        {
                                            path: 'findings',
                                            element: <ParticipantSourceDataFormSourceDataFindings />
                                        },
                                        {
                                            path: ':itemName',
                                            element: <ParticipantSourceDataItemValueAuditAndNotes />,
                                            children: [
                                                {
                                                    path: 'notes-and-queries',
                                                    children: [
                                                        {
                                                            index: true,
                                                            element: <ParticipantSourceDataItemValueNotesAndQueries />
                                                        },
                                                        {
                                                            path: 'add',
                                                            element: <ParticipantSourceDataItemValueNotesAndQueriesAdd />
                                                        }
                                                    ]
                                                },
                                                {
                                                    path: 'findings',
                                                    children: [
                                                        {
                                                            index: true,
                                                            element: <ParticipantSourceDataItemValueSourceDataFindings />
                                                        },
                                                        {
                                                            path: 'add',
                                                            element: <ParticipantSourceDataItemValueSourceDataFindingsAdd />
                                                        },
                                                        {
                                                            path: ':sourceDataFindingId/resolve',
                                                            element: <ParticipantSourceDataItemValueSourceDataFindingsEdit />
                                                        }
                                                    ]
                                                },
                                                {
                                                    path: 'audit/data',
                                                    element: <ParticipantSourceDataItemValueAudit />
                                                },
                                                {
                                                    path: 'audit/review',
                                                    element: <ParticipantSourceDataItemValueReviewAudit />
                                                },
                                                {
                                                    path: 'audit/verification',
                                                    element: <ParticipantSourceDataItemValueVerificationAudit />
                                                },
                                                {
                                                    path: 'audit/not-applicable',
                                                    element: <ParticipantSourceDataItemValueNotApplicableAudit />
                                                }
                                            ]
                                        },
                                        {
                                            path: ':itemGroupName/:itemGroupRepeat/:itemName',
                                            element: <ParticipantSourceDataItemValueAuditAndNotes />,
                                            children: [
                                                {
                                                    path: 'notes-and-queries',
                                                    children: [
                                                        {
                                                            index: true,
                                                            element: <ParticipantSourceDataItemValueNotesAndQueries />
                                                        },
                                                        {
                                                            path: 'add',
                                                            element: <ParticipantSourceDataItemValueNotesAndQueriesAdd />
                                                        }
                                                    ]
                                                },
                                                {
                                                    path: 'findings',
                                                    children: [
                                                        {
                                                            index: true,
                                                            element: <ParticipantSourceDataItemValueSourceDataFindings />
                                                        },
                                                        {
                                                            path: 'add',
                                                            element: <ParticipantSourceDataItemValueSourceDataFindingsAdd />
                                                        },
                                                        {
                                                            path: ':sourceDataFindingId/resolve',
                                                            element: <ParticipantSourceDataItemValueSourceDataFindingsEdit />
                                                        }
                                                    ]
                                                },
                                                {
                                                    path: 'audit/data',
                                                    element: <ParticipantSourceDataItemValueAudit />
                                                },
                                                {
                                                    path: 'audit/review',
                                                    element: <ParticipantSourceDataItemValueReviewAudit />
                                                },
                                                {
                                                    path: 'audit/verification',
                                                    element: <ParticipantSourceDataItemValueVerificationAudit />
                                                },
                                                {
                                                    path: 'audit/not-applicable',
                                                    element: <ParticipantSourceDataItemValueNotApplicableAudit />
                                                }
                                            ]
                                        }
                                    ]
                                }
                            ]
                        }
                    ]
                }
            ]
        }
    ]
}

const participantRoutes: RouteObject = {
    path: 'participants',
    children: [
        {
            index: true,
            element: <Participants />
        },
        {
            path: ':studyNumber/report',
            element: <SourceDataReport />
        },
        {
            path: ':studyNumber',
            element: <ParticipantSourceData />,
            children: [
                {
                    index: true,
                    element: <RedirectParticipantSourceData />
                },
                {
                    path: 'notes-and-queries',
                    element: <ParticipantNotesAndQueries />
                },
                {
                    path: 'findings',
                    children: [
                        {
                            index: true,
                            element: <ParticipantSourceDataFindings />
                        },
                        {
                            path: 'add',
                            element: <ParticipantSourceDataFindingsAdd />
                        },
                        {
                            path: ':sourceDataFindingId/resolve',
                            element: <ParticipantSourceDataFindingsEdit />
                        }
                    ]
                },
                participantSourceDataRoutes,
                {
                    path: 'forms/:caseReportFormOid',
                    element: <ParticipantSourceDataFormTable />
                },
                {
                    path: 'forms/grid',
                    element: <ParticipantSourceDataFormTableV2 />
                }
            ]
        }
    ]
};

const institutionRoutes: RouteObject = {
    path: 'institutions',
    children: [
        {
            index: true,
            element: <Institutions />
        },
        {
            path: ':institutionCode',
            children: [
                {
                    index: true,
                    element: <RedirectInstitution />
                },
                participantRoutes,
                {
                    path: 'notes-and-queries',
                    element: <InstitutionNotesAndQueries />
                },
                {
                    path: 'findings',
                    children: [
                        {
                            index: true,
                            element: <InstitutionSourceDataFindings />
                        },
                        {
                            path: 'add',
                            element: <InstitutionSourceDataFindingsAdd />
                        },
                        {
                            path: ':sourceDataFindingId/resolve',
                            element: <InstitutionSourceDataFindingsEdit />
                        }
                    ]
                },
                {
                    path: 'report',
                    element: <SourceDataReport />
                }
            ]
        },
    ]
};


const configurationTrialRoutes: RouteObject = {
    path: 'trial',
    element: <ConfigurationTrial />
};

const configurationMasterGroupRoutes: RouteObject = {
    path: 'master-groups',
    children: [
        {
            index: true,
            element: <ConfigurationMasterGroups />
        },
        {
            path: ':masterGroupCode',
            element: <ConfigurationMasterGroup />
        },
    ]
};

const configurationCoordinatingGroupRoutes: RouteObject = {
    path: 'coordinating-groups',
    children: [
        {
            index: true,
            element: <ConfigurationCoordinatingGroups />
        },
        {
            path: ':coordinatingGroupCode',
            element: <ConfigurationCoordinatingGroup />
        },
    ]
};


const configurationInstitutionRoutes: RouteObject = {
    path: 'institutions',
    children: [
        {
            index: true,
            element: <ConfigurationInstitutions />
        },
        {
            path: ':institutionCode',
            children: [
                {
                    index: true,
                    element: <ConfigurationInstitution />
                },
                {
                    path: 'participants',
                    children: [
                        {
                            index: true,
                            element: <ConfigurationParticipants />
                        },
                        {
                            path: ':studyNumber',
                            element: <ConfigurationParticipant />
                        },
                    ]
                }
            ]
        },
    ]
};

const configurationParticipantRoutes: RouteObject = {
    path: 'participants',
    children: [
        {
            index: true,
            element: <ConfigurationParticipants />
        },
    ]
};

const configurationSourceDataFormItemValueRoutes: RouteObject = {
    path: 'values',
    children: [
        {
            index: true,
            element: <ConfigurationSourceDataItemValues />,
        },
        {
            path: ':itemValue',
            children: [
                {
                    index: true,
                    element: <ConfigurationSourceDataItemValue />
                }
            ]
        }
    ]
}

const configurationSourceDataItemRoutes: RouteObject = {
    path: 'items',
    children: [
        {
            index: true,
            element: <ConfigurationSourceDataItems />,
        },
        {
            path: ':itemName',
            children: [
                {
                    index: true,
                    element: <ConfigurationSourceDataItem />
                },
                configurationSourceDataFormItemValueRoutes
            ]
        }
    ]
}

const configurationSourceDataItemGroupRoutes: RouteObject = {
    path: 'groups',
    children: [
        {
            index: true,
            element: <RedirectConfigurationSourceDataItemGroup />,
        },
        {
            path: ':itemGroupName',
            children: [
                {
                    index: true,
                    element: <ConfigurationSourceDataItemGroup />
                },
                configurationSourceDataItemRoutes
            ]
        }
    ]
}

const configurationSourceDataFormFormVersionRoutes: RouteObject = {
    path: 'versions',
    children: [
        {
            index: true,
            element: <ConfigurationSourceDataFormVersions />,
        },
        {
            path: ':caseReportFormVersionOid',
            children: [
                {
                    index: true,
                    element: <ConfigurationSourceDataFormVersion />
                },
                configurationSourceDataItemRoutes,
                configurationSourceDataItemGroupRoutes
            ]
        }
    ]
}

const configurationSourceDataFormFormRoutes: RouteObject = {
    path: 'forms',
    children: [
        {
            index: true,
            element: <ConfigurationSourceDataForms />,
        },
        {
            path: ':caseReportFormOid',
            children: [
                {
                    index: true,
                    element: <ConfigurationSourceDataForm />
                },
                configurationSourceDataFormFormVersionRoutes
            ]
        }
    ]
}


const configurationSourceDataFormEventRoutes: RouteObject = {
    path: 'events',
    children: [
        {
            index: true,
            element: <ConfigurationSourceDataEvents />,
        },
        {
            path: ':studyEventOid',
            children: [
                {
                    index: true,
                    element: <ConfigurationSourceDataEvent />
                },
                configurationSourceDataFormFormRoutes
            ]
        }
    ]
}

const configurationSourceDataFormRoutes: RouteObject[] = [
    {
        index: true,
        element: <ConfigurationSourceDataDetails />
    },
    configurationSourceDataFormEventRoutes,
    configurationSourceDataFormFormRoutes
];

const configurationSourceDataRoutes: RouteObject = {
    path: 'source-data',
    children: [
        {
            index: true,
            element: <ConfigurationSourceDatas />
        },
        {
            path: 'new',
            element: <ConfigurationSourceData insert={true} />,
            children: configurationSourceDataFormRoutes
        },
        {
            path: 'copy/:copyConfigurationSourceDataId',
            element: <ConfigurationSourceData insert={true} />,
            children: configurationSourceDataFormRoutes
        },
        {
            path: 'trial',
            element: <ConfigurationSourceData global={true} />,
            children: configurationSourceDataFormRoutes
        },
        {
            path: ':configurationSourceDataId',
            element: <ConfigurationSourceData />,
            children: configurationSourceDataFormRoutes
        }
    ]
};

const configurationRoutes: RouteObject = {
    path: 'configuration',
    children: [
        {
            index: true,
            element: <Configuration />
        },
        {
            path: 'utilities',
            element: <ConfigurationUtilities />
        },
        configurationTrialRoutes,
        configurationMasterGroupRoutes,
        configurationCoordinatingGroupRoutes,
        configurationInstitutionRoutes,
        configurationParticipantRoutes,
        configurationSourceDataRoutes
    ]
};

const trialRoutes: RouteObject = {
    path: ':trialOid',
    children: [
        {
            index: true,
            element: <RedirectTrial />
        },
        participantRoutes,
        institutionRoutes,
        {
            path: 'notes-and-queries',
            children: [
                {
                    index: true,
                    element: <TrialNotesAndQueries />
                },
                {
                    path: 'bulk-add',
                    element: <TrialNotesAndQueriesBulkAdd />
                }
            ] 
        },
        {
            path: 'findings',
            children: [
                {
                    index: true,
                    element: <TrialSourceDataFindings />
                },
                {
                    path: 'add',
                    element: <TrialSourceDataFindingsAdd />
                },
                {
                    path: ':sourceDataFindingId/resolve',
                    element: <TrialSourceDataFindingsEdit />
                }
            ]
        },
        {
            path: 'report',
            element: <SourceDataReport />
        },
        configurationRoutes
    ]
}

const routes: RouteObject[] = [
    {
        path: "/",
        element: <AppLayout />,
        ErrorBoundary: RouteErrorBoundary,
        children: [
            {
                index: true,
                element: <Trials />
            },
            trialRoutes
        ],
    },
];

const App: FC = () => {

    const { data: user } = useContext(AuthenticatedUserContext);

    const isConfigurationAdmin = useMemo(() => {
        return user?.roles?.includes('ConfigurationAdmin');
    }, [user]);

    const routes = useMemo(() => {
        const trialRoutes: RouteObject = {
            path: ':trialOid',
            children: [
                {
                    index: true,
                    element: <RedirectTrial />
                },
                participantRoutes,
                institutionRoutes,
                {
                    path: 'notes-and-queries',
                    children: [
                        {
                            index: true,
                            element: <TrialNotesAndQueries />
                        },
                        {
                            path: 'bulk-add',
                            element: <TrialNotesAndQueriesBulkAdd />
                        }
                    ] 
                },
                {
                    path: 'findings',
                    children: [
                        {
                            index: true,
                            element: <TrialSourceDataFindings />
                        },
                        {
                            path: 'add',
                            element: <TrialSourceDataFindingsAdd />
                        },
                        {
                            path: ':sourceDataFindingId/resolve',
                            element: <TrialSourceDataFindingsEdit />
                        }
                    ]
                },
                {
                    path: 'report',
                    element: <SourceDataReport />
                },
            ]
        }

        if(isConfigurationAdmin) {
            trialRoutes.children?.push(configurationRoutes)
        }

        const routes: RouteObject[] = [
            {
                path: "/",
                element: <AppLayout />,
                ErrorBoundary: RouteErrorBoundary,
                children: [
                    {
                        index: true,
                        element: <Trials />
                    },
                    trialRoutes
                ],
            },
        ];

        return routes;
    }, [isConfigurationAdmin, participantRoutes, institutionRoutes, configurationRoutes]);

    const router = useMemo(() => Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)(routes), [routes]);

    return (
        <RouterProvider router={router} />
    )
}

// const App: FC = () => {
//     return (
//         <Routes>
//             <Route>
//                 <Route path="/" element={<AppLayout />}>
//                     <Route index element={<Trials />} />
//                     <Route path=":trialOid">
//                         <Route index element={<RedirectTrial />} />
//                         <Route path="participants">
//                             <Route index element={<Participants />} />
//                         </Route>
//                         <Route path="institutions">
//                             <Route index element={<Institutions />} />
//                             <Route path=":institutionCode">
//                                 <Route index element={<RedirectInstitution />} />
//                                 <Route path="participants">
//                                     <Route index element={<Participants />} />
//                                 </Route>
//                                 <Route path="notes-and-queries" element={<InstitutionNotesAndQueries />} />
//                             </Route>
//                         </Route>
//                         <Route path="notes-and-queries" element={<TrialNotesAndQueries />} />
//                         <Route path="configuration">
//                             <Route index element={<Configuration />} />
//                             <Route path="trial" element={<ConfigurationTrial />} />
//                             <Route path="master-groups">
//                                 <Route index element={<ConfigurationMasterGroups />} />
//                                 <Route path=":masterGroupCode" element={<ConfigurationMasterGroup />} />
//                             </Route>
//                             <Route path="coordinating-groups">
//                                 <Route index element={<ConfigurationCoordinatingGroups />} />
//                                 <Route path=":coordinatingGroupCode" element={<ConfigurationCoordinatingGroup />} />
//                             </Route>
//                             <Route path="institutions">
//                                 <Route index element={<ConfigurationInstitutions />} />
//                                 <Route path=":institutionCode">
//                                     <Route index element={<ConfigurationInstitution />} />
//                                     <Route path="participants">
//                                         <Route index element={<ConfigurationParticipants />} />
//                                         <Route path=":studyNumber" element={<ConfigurationParticipant />} />
//                                     </Route>
//                                 </Route>
//                             </Route>
//                             <Route path="participants" element={<ConfigurationParticipants />} />
//                             <Route path="source-data">
//                                 <Route index element={<ConfigurationSourceDatas />} />
//                                 <Route path=":configurationSourceDataId" element={<ConfigurationSourceData />}>
//                                     <Route index element={<ConfigurationSourceDataDetails />} />
//                                     <Route path="details" element={<ConfigurationSourceDataDetails />} />
//                                     <Route path="events">
//                                         <Route index element={<ConfigurationSourceDataEvents />} />
//                                         <Route path=":studyEventOid">
//                                             <Route index element={<ConfigurationSourceDataEvent />} />
//                                             <Route path="forms">
//                                                 <Route index element={<ConfigurationSourceDataForms />} />
//                                                 <Route path=":caseReportFormOid">
//                                                     <Route index element={<ConfigurationSourceDataForm />} />
//                                                     <Route path="versions">
//                                                         <Route index element={<ConfigurationSourceDataFormVersions />} />
//                                                         <Route path=":caseReportFormVersionOid">
//                                                             <Route index element={<ConfigurationSourceDataFormVersion />} />
//                                                             <Route path="items">
//                                                                 <Route index element={<ConfigurationSourceDataItems />} />
//                                                                 <Route path=":itemName">
//                                                                     <Route index element={<ConfigurationSourceDataItem />} />
//                                                                     <Route path="values">
//                                                                         <Route index element={<ConfigurationSourceDataItemValues />} />
//                                                                         <Route path=":itemValue">
//                                                                             <Route index element={<ConfigurationSourceDataItemValue />} />
//                                                                         </Route>
//                                                                     </Route>
//                                                                 </Route>
//                                                             </Route>
//                                                             <Route path="groups">
//                                                                 <Route path=':itemGroupName'>
//                                                                     <Route index element={<ConfigurationSourceDataItemGroup />} />    
//                                                                     <Route path="items">
//                                                                         <Route index element={<ConfigurationSourceDataItems />} />
//                                                                         <Route path=":itemName">
//                                                                             <Route index element={<ConfigurationSourceDataItem />} />
//                                                                             <Route path="values">
//                                                                                 <Route index element={<ConfigurationSourceDataItemValues />} />
//                                                                                 <Route path=":itemValue">
//                                                                                     <Route index element={<ConfigurationSourceDataItemValue />} />
//                                                                                 </Route>
//                                                                             </Route>
//                                                                         </Route>
//                                                                     </Route>
//                                                                 </Route>
//                                                             </Route>
//                                                         </Route>
//                                                     </Route>
//                                                 </Route>
//                                             </Route>
//                                         </Route>
//                                     </Route>
//                                     <Route path="forms">
//                                         <Route index element={<ConfigurationSourceDataForms />} />
//                                         <Route path=":caseReportFormOid">
//                                             <Route index element={<ConfigurationSourceDataForm />} />
//                                             <Route path="versions">
//                                                 <Route index element={<ConfigurationSourceDataFormVersions />} />
//                                                 <Route path=":caseReportFormVersionOid">
//                                                     <Route index element={<ConfigurationSourceDataFormVersion />} />
//                                                     <Route path="items">
//                                                         <Route index element={<ConfigurationSourceDataItems />} />
//                                                         <Route path=":itemName">
//                                                             <Route index element={<ConfigurationSourceDataItem />} />
//                                                             <Route path="values">
//                                                                 <Route index element={<ConfigurationSourceDataItemValues />} />
//                                                                 <Route path=":itemValue">
//                                                                     <Route index element={<ConfigurationSourceDataItemValue />} />
//                                                                 </Route>
//                                                             </Route>
//                                                         </Route>
//                                                     </Route>
//                                                     <Route path="groups">
//                                                         <Route path=':itemGroupName'>
//                                                             <Route index element={<ConfigurationSourceDataItemGroup />} /> 
//                                                             <Route path="items">
//                                                                 <Route index element={<ConfigurationSourceDataItems />} />
//                                                                 <Route path=":itemName">
//                                                                     <Route index element={<ConfigurationSourceDataItem />} />
//                                                                     <Route path="values">
//                                                                         <Route index element={<ConfigurationSourceDataItemValues />} />
//                                                                         <Route path=":itemValue">
//                                                                             <Route index element={<ConfigurationSourceDataItemValue />} />
//                                                                         </Route>
//                                                                     </Route>
//                                                                 </Route>
//                                                             </Route>
//                                                         </Route>
//                                                     </Route>
//                                                 </Route>
//                                             </Route>
//                                         </Route>
//                                     </Route>
//                                 </Route>
//                             </Route>
//                         </Route>
//                     </Route>
//                 </Route>
//             </Route>
//         </Routes>
//     )
// };

export default App;